import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { getTargetSearchFeedbackCue } from "@/audio/curriculum-catalog/focus-social-feedback-cues";
import {
  createTargetSearchState,
  getActiveTargetSearchRound,
  getTargetSearchFocusableIds,
  reduceTargetSearch,
  scoreTargetSearchState,
  targetSearchPayloadSchema,
  type TargetSearchAction,
  type TargetSearchPayload,
  type TargetSearchState
} from "@/game/templates/search";

export type FocusSocialTargetSearchAction =
  | TargetSearchAction
  | { readonly type: "replay" };

export type FocusSocialTargetSearchShape =
  | "ball"
  | "cup"
  | "kite"
  | "leaf"
  | "token";

export type FocusSocialTargetSearchSwatch =
  | "blue"
  | "green"
  | "neutral"
  | "red"
  | "yellow";

export interface FocusSocialTargetSearchObjectModel {
  readonly id: string;
  readonly label: string;
  readonly shape: FocusSocialTargetSearchShape;
  readonly swatch: FocusSocialTargetSearchSwatch;
  readonly isAvailable: boolean;
  readonly isFocused: boolean;
  readonly isFound: boolean;
  readonly isHinted: boolean;
}

export interface FocusSocialTargetSearchRoundModel {
  readonly audioPath: string | null;
  readonly id: string;
  readonly prompt: string;
  readonly number: number;
  readonly total: number;
  readonly foundTargetCount: number;
  readonly targetCount: number;
  readonly objects: readonly FocusSocialTargetSearchObjectModel[];
}

export interface FocusSocialTargetSearchFeedbackModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly message: string;
  readonly playbackRequest: number;
}

export interface FocusSocialTargetSearchResultModel {
  readonly metrics: ActivityCompletionMetrics;
}

export interface FocusSocialTargetSearchRenderModel {
  readonly status: TargetSearchState["status"];
  readonly focusedId: string | null;
  readonly round: FocusSocialTargetSearchRoundModel | null;
  readonly feedback: FocusSocialTargetSearchFeedbackModel | null;
  readonly result: FocusSocialTargetSearchResultModel | null;
}

interface CssObjectAppearance {
  readonly shape: FocusSocialTargetSearchShape;
  readonly swatch: FocusSocialTargetSearchSwatch;
}

const CSS_OBJECT_APPEARANCES: Readonly<Record<string, CssObjectAppearance>> = {
  "blue-cup": { shape: "cup", swatch: "blue" },
  "green-leaf": { shape: "leaf", swatch: "green" },
  "red-ball": { shape: "ball", swatch: "red" },
  "yellow-kite": { shape: "kite", swatch: "yellow" }
};

const FALLBACK_OBJECT_APPEARANCE: CssObjectAppearance = {
  shape: "token",
  swatch: "neutral"
};

function getCompletionMetrics(
  state: TargetSearchState
): ActivityCompletionMetrics {
  const score = scoreTargetSearchState(state);

  return {
    completed: state.status === "completed",
    score: score.score,
    correctRate: score.correctRate,
    hintCount: score.hintCount,
    retryCount: score.retryCount
  };
}

function toFeedbackModel(
  state: TargetSearchState
): FocusSocialTargetSearchFeedbackModel | null {
  const stage = state.hintState.stage;

  if (stage === "none") {
    return null;
  }

  if (state.lastFeedback !== null) {
    const cue = getTargetSearchFeedbackCue(stage, "retry");

    return {
      audioPath: cue.audioPath,
      kind: "retry",
      message: cue.text,
      playbackRequest: state.hintCount
    };
  }

  if (state.highlightedTargetId !== null) {
    const cue = getTargetSearchFeedbackCue(stage, "hint");

    return {
      audioPath: cue.audioPath,
      kind: "hint",
      message: cue.text,
      playbackRequest: state.hintCount
    };
  }

  return null;
}

function keepKeyboardFocusAvailable(
  state: TargetSearchState
): TargetSearchState {
  if (
    state.status === "completed" ||
    state.lastInputMethod !== "keyboard" ||
    state.focusedId === null
  ) {
    return state;
  }

  const focusableIds = getTargetSearchFocusableIds(state);

  if (focusableIds.includes(state.focusedId)) {
    return state;
  }

  return {
    ...state,
    focusedId: focusableIds[0] ?? null
  };
}

function toRenderModel(
  state: TargetSearchState
): FocusSocialTargetSearchRenderModel {
  const activeRound = getActiveTargetSearchRound(state);
  const focusableIds = new Set(getTargetSearchFocusableIds(state));
  const foundIds = new Set(state.foundTargetIds);
  const round =
    activeRound === null
      ? null
      : {
          audioPath: activeRound.promptAudio ?? null,
          id: activeRound.id,
          prompt: activeRound.prompt,
          number: state.currentRoundIndex + 1,
          total: state.rounds.length,
          foundTargetCount: state.foundTargetIds.length,
          targetCount: activeRound.targetIds.length,
          objects: activeRound.visibleObjects.map((object) => {
            const appearance =
              CSS_OBJECT_APPEARANCES[object.id] ??
              FALLBACK_OBJECT_APPEARANCE;

            return {
              id: object.id,
              label: object.label,
              shape: appearance.shape,
              swatch: appearance.swatch,
              isAvailable: focusableIds.has(object.id),
              isFocused: state.focusedId === object.id,
              isFound: foundIds.has(object.id),
              isHinted: state.highlightedTargetId === object.id
            };
          })
        };
  const metrics = getCompletionMetrics(state);

  return {
    status: state.status,
    focusedId: state.focusedId,
    round,
    feedback: toFeedbackModel(state),
    result: metrics.completed ? { metrics } : null
  };
}

export const focusSocialTargetSearchAdapter: ActivityAdapter<
  TargetSearchPayload,
  TargetSearchState,
  FocusSocialTargetSearchAction,
  FocusSocialTargetSearchRenderModel
> = {
  activityType: "target-search",
  parsePayload: (value) => targetSearchPayloadSchema.parse(value),
  createInitialState: (payload, context) => {
    void context;
    return createTargetSearchState(payload);
  },
  reduce: (state, payload, action, context) => {
    void context;

    if (action.type === "replay") {
      return createTargetSearchState(payload);
    }

    return keepKeyboardFocusAvailable(reduceTargetSearch(state, action));
  },
  getMetrics: (state, payload) => {
    void payload;
    return getCompletionMetrics(state);
  },
  toRenderModel: (state, payload) => {
    void payload;
    return toRenderModel(state);
  }
};
